import { DeviceIntegrationModel } from '@ekkogmbh/apisdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createContextHardened } from 'src/Common/Helper/ContextHelper';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';

// Logo assets follow the convention in src/assets/images/integrations/README.md:
// `<system-key>-logo.*` (wide lockup) + `<system-key>-mark.*` (square tile mark).
import deephubLogo from 'src/assets/images/integrations/deephub-logo.png';
import deephubMark from 'src/assets/images/integrations/deephub-mark.png';
import solumAimsLogo from 'src/assets/images/integrations/solum-aims-logo.png';
import omloxHubMark from 'src/assets/images/integrations/omlox-hub-mark.svg';
import sepiooLogo from 'src/assets/images/integrations/sepioo-logo.svg';
import sepiooMark from 'src/assets/images/integrations/sepioo-mark.svg';
import solumAimsSaasLogo from 'src/assets/images/integrations/solum-aims-saas-logo.png';
import steuteSensorbridgeMark from 'src/assets/images/integrations/steute-sensorbridge-mark.png';

// @TODO all this should not be hard coded here
// @TODO device <-> provider info lookup route?

export const INTEGRATIONS = [
  'PdiDigital-Label',
  'Solum-Label',
  'Solum-Cloud-Label',
  'Steute-Switch',
  // 'Omlox',
  'Simulated-Label',
  'Virtual-Device',
];

type Manufacturer = {
  name: string;
  mark?: string;
};

const MANUFACTURER_MAP: Record<string, Manufacturer> = {
  solum: { name: 'SoluM' },
  pdi: { name: 'PDI Digitial', mark: sepiooMark },
  steute: { name: 'Steute', mark: steuteSensorbridgeMark },
  flowcate: { name: 'Flowcate', mark: deephubMark },
  omlox: { name: 'omlox', mark: omloxHubMark },
  ekko: { name: 'Ekko' },
};

export type ServiceTechnology = {
  name: string;
  description?: string;
  manufacturer?: Manufacturer;
  logo?: string;
};

export const mapIntegrationTechnology = (integrationName: string): ServiceTechnology => {
  switch (integrationName) {
    case 'PdiDigital-Label':
      return {
        name: 'Sepioo',
        description: 'E-paper shelf labels and displays by PDI Digital.',
        manufacturer: MANUFACTURER_MAP['pdi'],
        logo: sepiooLogo,
      };
    case 'Solum-Label':
      return {
        name: 'AIMS',
        description: 'SoluM ESL platform (on premise).',
        logo: solumAimsLogo,
        manufacturer: MANUFACTURER_MAP['solum'],
      };
    case 'Solum-Cloud-Label':
      return {
        name: 'AIMS SaaS',
        description: 'SoluM ESL platform (cloud).',
        logo: solumAimsSaasLogo,
        manufacturer: MANUFACTURER_MAP['solum'],
      };
    case 'Steute-Switch':
      return {
        name: 'Sensor Bridge',
        description: 'Steute wireless switches and sensors via SensorBridge.',
        manufacturer: MANUFACTURER_MAP['steute'],
      };
    case 'Simulated-Label':
      return {
        name: 'Simulated',
        description: 'Fully integrated service for simulated, interactive labels.',
        manufacturer: MANUFACTURER_MAP['ekko'],
      };
    case 'Virtual-Device':
      return {
        name: 'Virtual',
        description: 'Service for virtual dummy labels. For testing purposes only.',
        manufacturer: MANUFACTURER_MAP['ekko'],
      };
    default:
      if (integrationName.startsWith('Omlox')) {
        return {
          name: 'DeepHub',
          description: 'Flowcate DeepHub RTLS — omlox hub for real-time location tracking.',
          manufacturer: MANUFACTURER_MAP['flowcate'],
          logo: deephubLogo,
        };
      }
      return { name: integrationName };
  }
};

interface Context {
  selectedIntegration?: DeviceIntegrationModel;
  integrations: DeviceIntegrationModel[];
  /** when loading integrations */
  isFetching: boolean;
  /** when saving or deleting the selected entry */
  isChanging: boolean;
  selectIntegration: (index?: number) => void;
  /** overwrites selected or creates new integration entry */
  saveIntegration: (
    name: string,
    coordinate: string,
    configuration: Record<string, unknown>,
    successCallback?: () => void,
  ) => void;
  /** deletes selected integration */
  deleteIntegration: (name: string, coordinate: string) => void;
  fetchIntegrations: () => void;
}

const [DeviceIntegrationsContext, useDeviceIntegrations] = createContextHardened<Context>('Device Integrations');
export default useDeviceIntegrations;

interface Props {
  children: React.ReactNode;
}
export const DeviceIntegrationsProvider = ({ children }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const [isFetching, startFetching] = useRequest<DeviceIntegrationModel[]>();
  const [isChanging, startChanging] = useRequest();

  const [integrations, setIntegrations] = useState<DeviceIntegrationModel[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
  const selectedIntegration = useMemo(
    () => (selectedIndex !== undefined ? integrations[selectedIndex] : undefined),
    [selectedIndex, integrations],
  );

  const fetchIntegrations = useCallback(() => {
    setSelectedIndex(undefined);

    startFetching(
      () => api.getDeviceIntegrations(),
      (result) => {
        setIntegrations(result ?? []);
      },
    );
  }, [api]);

  // on mount
  useEffect(() => {
    fetchIntegrations();
  }, []);

  const selectIntegration = useCallback(
    (index?: number) => {
      if (index === undefined || index >= integrations.length) {
        setSelectedIndex(undefined);
      } else {
        setSelectedIndex(index);
      }
    },
    [integrations],
  );

  const saveIntegration = useCallback(
    (name: string, coordinate: string, configuration: Record<string, unknown>, successCallback?: () => void) => {
      startChanging(
        () => api.saveDeviceIntegration(name, coordinate, configuration, selectedIntegration !== undefined),
        () => {
          if (successCallback) {
            successCallback();
          }
          fetchIntegrations();
        },
      );
    },
    [api, selectedIntegration],
  );

  const deleteIntegration = useCallback((name: string, coordinate: string) => {
    startChanging(
      () => api.deleteDeviceIntegration(name, coordinate),
      () => {
        selectIntegration(undefined);
        fetchIntegrations();
      },
    );
  }, []);

  return (
    <DeviceIntegrationsContext.Provider
      value={{
        selectedIntegration,
        integrations,
        isFetching,
        isChanging,
        fetchIntegrations,
        selectIntegration,
        saveIntegration,
        deleteIntegration,
      }}
    >
      {children}
    </DeviceIntegrationsContext.Provider>
  );
};
